import * as React from 'react';
import { Tooltip } from '../../primitives/Tooltip/Tooltip';
import { MemberChip } from '../MemberChip/MemberChip';
import type { DomainKey, Member } from '../_types';
import { Stack } from '../../layout/Stack/Stack';
import {
  root as rootCls,
  tooltipTitle as tooltipTitleCls,
  tooltipEyebrow as tooltipEyebrowCls,
} from './GovernanceTag.css';

export type GovernanceTagSize = 'sm' | 'md';

export interface GovernanceTagProps extends Omit<
  React.HTMLAttributes<HTMLSpanElement>,
  'children'
> {
  domain: DomainKey;
  size?: GovernanceTagSize;
  /** Short code ('ARCH') vs full label ('Architecture'). Default 'full'. */
  display?: 'full' | 'short';
  interactive?: boolean;
  reviewer?: Member | null;
  approver?: Member;
  onClick?: () => void;
  /** React 19 ref-as-prop. */
  ref?: React.Ref<HTMLSpanElement>;
}

const DOMAIN_FULL: Record<DomainKey, string> = {
  customerExperience: 'Customer Experience',
  productFunctionality: 'Product Functionality',
  commercial: 'Commercial',
  delivery: 'Cost / Delivery',
  architecture: 'Architecture',
  technology: 'Technology',
  implementation: 'Implementation',
  contracts: 'Contracts',
};

const DOMAIN_SHORT: Record<DomainKey, string> = {
  customerExperience: 'CX',
  productFunctionality: 'PRD',
  commercial: 'CML',
  delivery: 'DEL',
  architecture: 'ARCH',
  technology: 'TECH',
  implementation: 'IMPL',
  contracts: 'CON',
};

/**
 * GovernanceTag — small uppercase chip that names a domain
 * (Architecture / Commercial / Implementation / etc.) and optionally
 * surfaces the reviewer / approver as a tooltip-revealed `<MemberChip>`.
 *
 * Two display modes:
 *   - `full` — full label ("Architecture", "Customer Experience")
 *   - `short` — 3-4 letter code ("ARCH", "CX")
 *
 * Becomes interactive when `interactive={true}` — the chip becomes a
 * `role="button"` with Enter / Space activation, fires `onClick`, and
 * reveals the reviewer/approver tooltip on hover/focus.
 */
export function GovernanceTag({
  domain,
  size = 'md',
  display = 'full',
  interactive = false,
  reviewer,
  approver,
  onClick,
  'aria-label': ariaLabel,
  ref,
  className,
  ...rest
}: GovernanceTagProps) {
  const label = display === 'short' ? DOMAIN_SHORT[domain] : DOMAIN_FULL[domain];

  const cls = [rootCls({ domain, size, interactive }), className].filter(Boolean).join(' ');

  // A non-interactive tag is a plain text label — its visible text IS its
  // accessible name, so we must NOT emit `aria-label` / `role` / `tabIndex`
  // on it (a stray `role="button"`-free aria-label on a span overrides the
  // visible text for AT and adds noise). The reviewer/approver-rich label
  // only makes sense on the interactive (focusable, tooltip-revealing) chip.
  const content = (
    <span
      ref={ref}
      className={cls}
      role={interactive ? 'button' : undefined}
      tabIndex={interactive ? 0 : undefined}
      aria-label={
        interactive
          ? (ariaLabel ??
            `Domain: ${DOMAIN_FULL[domain]}${
              approver ? `, approver ${approver.name}` : ''
            }${reviewer ? `, reviewer ${reviewer.name}` : ''}`)
          : ariaLabel
      }
      onClick={interactive ? onClick : undefined}
      onKeyDown={
        interactive
          ? (e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                onClick?.();
              }
            }
          : undefined
      }
      {...rest}
    >
      {label}
    </span>
  );

  if (!interactive || (!reviewer && !approver)) {
    return content;
  }

  const tooltipContent = (
    <Stack gap={2}>
      <strong className={tooltipTitleCls}>{DOMAIN_FULL[domain]}</strong>
      {reviewer && (
        <div>
          <div className={tooltipEyebrowCls}>REVIEWER</div>
          <MemberChip member={reviewer} size="sm" />
        </div>
      )}
      {approver && (
        <div>
          <div className={tooltipEyebrowCls}>APPROVER</div>
          <MemberChip member={approver} size="sm" />
        </div>
      )}
    </Stack>
  );

  return <Tooltip content={tooltipContent}>{content}</Tooltip>;
}
GovernanceTag.displayName = 'GovernanceTag';
